import { useState } from 'react';
import { X, Copy, Download, Printer, AlertTriangle, Check } from 'lucide-react';

const BackupCodesModal = ({ isOpen, onClose, backupCodes }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen || !backupCodes) return null;

  const handleCopy = () => {
    const codesText = backupCodes.join('\n');
    navigator.clipboard.writeText(codesText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const codesText = backupCodes.join('\n');
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `jpro-backup-codes-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=600,height=400');
    printWindow.document.write(`
      <html>
        <head>
          <title>JPro Backup Codes</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
            }
            h1 {
              font-size: 24px;
              margin-bottom: 10px;
            }
            .warning {
              background: #fef2f2;
              border: 1px solid #fca5a5;
              padding: 15px;
              margin: 20px 0;
              border-radius: 8px;
            }
            .codes {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
              margin: 20px 0;
            }
            .code {
              font-family: monospace;
              font-size: 16px;
              padding: 10px;
              background: #f3f4f6;
              border-radius: 4px;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <h1>JPro Recovery Codes</h1>
          <p>Generated: ${new Date().toLocaleString()}</p>
          <div class="warning">
            <strong>⚠️ Important:</strong> Store these codes securely. Each code can only be used once.
          </div>
          <div class="codes">
            ${backupCodes.map(code => `<div class="code">${code}</div>`).join('')}
          </div>
          <p style="margin-top: 20px; font-size: 12px; color: #666;">
            If you lose access to your two-factor authentication device, you can use these codes to log in to your account.
          </p>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Backup Recovery Codes
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Warning Banner */}
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-6">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-orange-800 dark:text-orange-400">
                Save these codes in a secure location
              </h3>
              <p className="text-sm text-orange-700 dark:text-orange-500 mt-1">
                Each code can only be used once. If you lose access to your authenticator device, you can use these codes to log in to your account.
              </p>
            </div>
          </div>
        </div>

        {/* Backup Codes Grid */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          {backupCodes.map((code, index) => (
            <div
              key={index}
              className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg text-center"
            >
              <code className="text-lg font-mono text-gray-900 dark:text-white">
                {code}
              </code>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={handleCopy}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
          >
            {copied ? (
              <>
                <Check className="h-4 w-4" />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" />
                <span>Copy All</span>
              </>
            )}
          </button>

          <button
            onClick={handleDownload}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
          >
            <Download className="h-4 w-4" />
            <span>Download</span>
          </button>

          <button
            onClick={handlePrint}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium"
          >
            <Printer className="h-4 w-4" />
            <span>Print</span>
          </button>
        </div>

        {/* Important Notice */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            Important:
          </h4>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 list-disc list-inside">
            <li>Store these codes in a safe place (password manager, safe, etc.)</li>
            <li>Each code can only be used once</li>
            <li>You can regenerate new codes at any time from settings</li>
            <li>Regenerating codes will invalidate all previous codes</li>
          </ul>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium"
        >
          I've Saved My Codes
        </button>
      </div>
    </div>
  );
};

export default BackupCodesModal;
